/*
 * Venue text format: a small Markdown subset (venue editing ledger, V3).
 *   **bold**   *italic*   "- item" or "• item" bullets
 *   Blank line = new paragraph. One Enter = new line.
 * Host text is data: this file only builds a plain tree. VenueText turns it into
 * React elements. Never use dangerouslySetInnerHTML with venue text.
 * Every scan below is linear, so long or strange text cannot freeze the page.
 */

const BULLET = /^\s*(?:-\s+|•\s*)(.*)$/;
// Not a space and not ASCII punctuation. (A \p{L} pattern would add about 5 KB to every visitor's download.)
const NOT_WORD_CHAR = /[\s!-/:-@[-`{-~]/;
const SPACE = /\s/;

const isWordChar = (ch) => ch !== undefined && !NOT_WORD_CHAR.test(ch);
const isSpace = (ch) => ch === undefined || SPACE.test(ch);

// A mark opens a span only before text, and not inside a word ("5*3*2" stays as typed).
const canOpen = (text, at, size) => !isWordChar(text[at - 1]) && !isSpace(text[at + size]) && (size === 2 || text[at + 1] !== '*');
const canClose = (text, at, size) => !isSpace(text[at - 1]) && text[at - 1] !== '*' && !isWordChar(text[at + size]);

// Splits text on paired marks of `size` stars. Returns [{ text, marked }].
// A mark with no partner stays as typed.
const splitMarks = (text, size) => {
  const mark = '*'.repeat(size);
  const parts = [];
  let plainFrom = 0;
  let at = text.indexOf(mark);
  while (at !== -1) {
    if (!canOpen(text, at, size)) {
      at = text.indexOf(mark, at + 1);
      continue;
    }
    let close = text.indexOf(mark, at + size + 1);
    while (close !== -1 && !canClose(text, close, size)) close = text.indexOf(mark, close + 1);
    if (close === -1) break; // No partner after this mark, so none after any later mark.
    if (size === 2) while (text[close + 2] === '*') close += 1; // "***x***" → bold "*x*"
    if (at > plainFrom) parts.push({ text: text.slice(plainFrom, at), marked: false });
    parts.push({ text: text.slice(at + size, close), marked: true });
    plainFrom = close + size;
    at = text.indexOf(mark, plainFrom);
  }
  if (plainFrom < text.length) parts.push({ text: text.slice(plainFrom), marked: false });
  return parts;
};

// One line → [{ text, bold, italic }]
export const parseInline = (line) =>
  splitMarks(line, 2).flatMap(({ text, marked: bold }) => splitMarks(text, 1).map(({ text: part, marked: italic }) => ({ text: part, bold, italic })));

// Whole text → [{ type: 'p', lines: [inline] } | { type: 'ul', items: [inline] }]
export const parseVenueText = (text) => {
  const lines = String(text ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd());
  const blocks = [];
  let current = null;
  lines.forEach((line) => {
    if (!line.trim()) {
      current = null;
      return;
    }
    const bullet = line.match(BULLET);
    const type = bullet ? 'ul' : 'p';
    if (!current || current.type !== type) {
      current = type === 'ul' ? { type, items: [] } : { type, lines: [] };
      blocks.push(current);
    }
    if (bullet) {
      if (bullet[1].trim()) current.items.push(parseInline(bullet[1].trim()));
    } else {
      current.lines.push(parseInline(line.trim()));
    }
  });
  return blocks.filter((block) => (block.type === 'ul' ? block.items.length : block.lines.length));
};

// Text with the marks removed, for SEO and structured data (AEO task 14).
export const toPlainText = (text) => {
  const join = (inline) => inline.map((part) => part.text).join('');
  return parseVenueText(text)
    .map((block) => (block.type === 'ul' ? block.items.map((item) => `• ${join(item)}`) : block.lines.map(join)).join('\n'))
    .join('\n\n');
};
